import { useQuery } from '@tanstack/react-query';
import { billing, channelWa, identity } from '@repletoryreach/sdk-api';
import { useAuth } from '@repletoryreach/sdk-auth';
import type { ChecklistItem, Guide } from '../types';
import { builders } from './helpers';

// The Account guide (8 October 2026): Numbers, Channels, Billing, Refer and earn, Developers and Settings. Words
// under guide.account.*. Voice is left out while it is parked.

const { link, howLink, step, how, section, howTo } = builders('account');
const L = {
  numbers: link('numbers', '/numbers'),
  channels: link('channels', '/channels'),
  billing: link('billing', '/billing'),
  topup: link('topup', '/billing/topup'),
  alerts: link('alerts', '/billing/alerts'),
  usage: link('usage', '/billing/usage'),
  ledger: link('ledger', '/billing/ledger'),
  refer: link('refer', '/refer'),
  withdrawals: link('withdrawals', '/refer/withdrawals'),
  developers: link('developers', '/developers'),
  settings: link('settings', '/settings'),
  organisation: link('organisation', '/settings/organisation'),
  team: link('team', '/settings/team'),
  security: link('security', '/settings/security'),
  notifications: link('notifications', '/settings/notifications'),
  apiKeys: link('apiKeys', '/settings/api-keys'),
  webhooks: link('webhooks', '/settings/webhooks'),
  assistant: link('assistant', '/settings/assistant'),
  flows: link('flows', '/flows'),
  howConnect: howLink('howConnect', 'connect'),
  howTopup: howLink('howTopup', 'topup'),
  howAlerts: howLink('howAlerts', 'alerts'),
  howTeam: howLink('howTeam', 'team'),
  howSecurity: howLink('howSecurity', 'security'),
  howApi: howLink('howApi', 'api')
};

function useAccountChecklist(): { items: ChecklistItem[]; loading: boolean } {
  const { user } = useAuth();
  const numbers = useQuery({ queryKey: ['numbers'], queryFn: () => channelWa.listPhoneNumbers(), staleTime: 60_000 });
  const hours = useQuery({ queryKey: ['business-hours'], queryFn: () => identity.getBusinessHours(), staleTime: 60_000 });
  const wallet = useQuery({ queryKey: ['wallet'], queryFn: () => billing.getWallet(), staleTime: 30_000 });
  const people = useQuery({ queryKey: ['team', 'guide'], queryFn: () => identity.listUsers({ limit: 2 }), staleTime: 60_000 });
  const item = (id: string, to: string, done: boolean | undefined, optional = false): ChecklistItem => ({
    id, to, done, optional, titleKey: `guide.account.check.${id}.title`, hintKey: `guide.account.check.${id}.hint`
  });
  const items = [
    item('number', '/numbers', numbers.data ? (numbers.data.data?.length ?? 0) > 0 : undefined),
    item('organisation', '/settings/organisation', hours.data ? hours.data.data?.configured === true : undefined),
    item('credits', '/billing/topup', wallet.data ? (wallet.data.data?.credits_minor ?? 0) > 0 : undefined),
    item('alerts', '/billing/alerts', undefined),
    item('security', '/settings/security', user ? user.two_factor_enabled === true : undefined),
    item('team', '/settings/team', people.data ? (people.data.meta?.total ?? people.data.data?.length ?? 0) > 1 : undefined, true)
  ];
  return { items, loading: [numbers, hours, wallet, people].some((q) => q.isPending) };
}

export const ACCOUNT_GUIDE: Guide = {
  id: 'account',
  version: 1,
  titleKey: 'guide.account.title',
  introKey: 'guide.account.intro',
  routes: ['/numbers', '/channels', '/billing', '/refer', '/developers', '/settings'],
  tour: [
    step('welcome', undefined),
    step('numbers', '/numbers', 'numbers-connect', [L.howConnect]),
    step('numberList', '/numbers', 'numbers-list'),
    step('channels', '/channels', 'channels-connect'),
    step('otherChannels', '/channels', 'channels-other', [L.flows]),
    step('balance', '/billing', 'billing-balance'),
    step('plan', '/billing', 'billing-plan'),
    step('month', '/billing', 'billing-month', [L.usage, L.ledger]),
    step('topup', '/billing/topup', 'topup-form', [L.howTopup]),
    step('alerts', '/billing/alerts', 'alerts-low', [L.howAlerts]),
    step('refer', '/refer', 'refer-how,refer-metrics'),
    step('developers', '/developers', 'dev-tabs', [L.apiKeys, L.webhooks]),
    step('settings', '/settings', 'settings-nav'),
    step('team', '/settings/team', 'team-invite,team-list', [L.howTeam]),
    step('security', '/settings/security', 'security-2fa', [L.howSecurity]),
    step('organisation', '/settings/organisation', 'org-hours'),
    step('help', '/settings', 'account-help')
  ],
  howTos: [
    howTo('connect', [
      how('connect', 'button', '/numbers', 'numbers-connect'),
      how('connect', 'after', '/numbers', 'numbers-list'),
      how('connect', 'profile', '/numbers', 'numbers-list')
    ]),
    howTo('topup', [
      how('topup', 'amount', '/billing/topup', 'topup-form'),
      how('topup', 'pay', '/billing/topup', 'topup-form'),
      how('topup', 'after', '/billing', 'billing-balance', { links: [L.ledger] })
    ]),
    howTo('alerts', [
      how('alerts', 'low', '/billing/alerts', 'alerts-low', { links: [L.notifications] }),
      how('alerts', 'auto', '/billing/alerts', 'alerts-auto')
    ]),
    howTo('team', [
      how('team', 'invite', '/settings/team', 'team-invite,team-list'),
      how('team', 'roles', '/settings/team', 'team-list')
    ]),
    howTo('security', [
      how('security', 'password', '/settings/security', 'security-password'),
      how('security', 'twofa', '/settings/security', 'security-2fa'),
      how('security', 'sessions', '/settings/security', 'security-sessions')
    ]),
    howTo('api', [
      how('api', 'key', '/settings/api-keys', 'apikeys'),
      how('api', 'docs', '/developers', 'dev-key,dev-tabs'),
      how('api', 'webhooks', '/settings/webhooks', 'webhooks')
    ])
  ],
  sections: [
    section('overview', 2, 1, []),
    section('numbers', 3, 2, [L.numbers, L.howConnect]),
    section('channels', 3, 1, [L.channels, L.flows]),
    section('credits', 3, 1, [L.billing, L.topup]),
    section('plans', 3, 1, [L.billing]),
    // Extra use on Growth and Scale (30 October 2026).
    section('extra', 3, 1, [L.billing]),
    section('spend', 2, 1, [L.usage, L.ledger]),
    section('alerts', 2, 1, [L.alerts, L.howAlerts]),
    section('refer', 3, 1, [L.refer, L.withdrawals]),
    section('developers', 3, 1, [L.developers, L.apiKeys, L.webhooks]),
    section('team', 3, 1, [L.team, L.howTeam]),
    section('security', 2, 1, [L.security, L.howSecurity]),
    section('organisation', 3, 1, [L.organisation]),
    section('settings', 3, 0, [L.notifications, L.assistant, L.settings]),
    section('trouble', 6, 0, [L.numbers, L.topup, L.security])
  ],
  useChecklist: useAccountChecklist
};
